// features/finances/components/PresupuestoIngresosPanel.tsx
// ============================================================================
// FINANZAS › PRESUPUESTO › INGRESOS — objetivo de ingresos por categoría × mes (B2 ampliado, 25-oct-2026).
//
// Mabel (23-oct 15:18) viene «con los presupuestos del siguiente semestre». Hasta hoy el presupuesto de ingresos era solo
// mensualidades = activos × plan (calculado) y los demás conceptos iban «sin presupuesto». Aquí captura la META por
// categoría (las 8 filas de su hoja) y mes, con la misma mecánica que el presupuesto de gastos (desde este mes / solo este
// mes, copiar mes, copiar periodo). Debajo de cada objetivo se ve lo OPERATIVO (lo que calcula el sistema) y lo REAL
// devengado, y al final el comparativo del mes. Reportes › Presupuesto vs Real toma el objetivo capturado como
// presupuesto de esa fila. API: presupuestoIngresosApi.ts (lib/letsmovePresupuestoIngresos.js).
// ============================================================================
import { Fragment, useCallback, useEffect, useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight, Copy, Info, Repeat } from 'lucide-react';
import { moneyEntero as money } from '../api';
import {
  getCuadriculaIngresos, getComparativoIngresos, fijarMontoIngreso, quitarMontoIngreso, copiarMesAnteriorIngresos,
  PICuadricula, PIFila, PICelda, PIComparativo,
} from '../presupuestoIngresosApi';
import CopiarPeriodoDialog, { Modal, mesLargo, mesCorto, sumaMeses } from './CopiarPeriodoDialog';
import { Skeleton } from '../../../components/Skeleton';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';

const mesLocal = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' }).slice(0, 7);
const sinCentavos = (n: number) => '$' + Math.round(n).toLocaleString('es-MX');
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40';
const N_MESES = 6;
const signo = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '') + money(Math.abs(n));
const pctTxt = (p: number | null) => (p == null ? '' : `${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p).toFixed(1)} %`);
// En INGRESOS, quedarse por debajo del objetivo es lo malo (rojo); pasarse es bueno (verde).
const colorDif = (v: number | null) => (v == null ? 'text-content-3' : v < -0.005 ? 'text-red-600 dark:text-red-400' : v > 0.005 ? 'text-emerald-700 dark:text-emerald-400' : 'text-content-3');

type Edit = { fila: PIFila; mes: string; celda: PICelda | null };

export default function PresupuestoIngresosPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const puedeEditar = user?.role === 'admin';
  const [desde, setDesde] = useState(() => sumaMeses(mesLocal(), -2));
  const [d, setD] = useState<PICuadricula | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Edit | null>(null);
  const [copiar, setCopiar] = useState<string | null>(null);
  const [copiarPeriodo, setCopiarPeriodo] = useState(false);
  const [verDetalle, setVerDetalle] = useState(true);
  const [mesComp, setMesComp] = useState(mesLocal);
  const [version, setVersion] = useState(0);

  const cargar = useCallback(() => {
    setCargando(true); setError(null);
    getCuadriculaIngresos(desde, N_MESES)
      .then((r) => { setD(r); setVersion((v) => v + 1); })
      .catch((e: any) => setError(Number(e?.status) === 404 ? 'El presupuesto de ingresos se activa cuando el servidor termine de actualizarse.' : (e?.message || 'No se pudo cargar el presupuesto')))
      .finally(() => setCargando(false));
  }, [desde]);
  useEffect(() => { cargar(); }, [cargar]);

  async function hacerCopia(mes: string) {
    try {
      const r = await copiarMesAnteriorIngresos(mes);
      toast.success(r.cambiados ? `${mesLargo(mes)} quedó igual que ${mesLargo(r.desde)} (${r.cambiados} concepto${r.cambiados === 1 ? '' : 's'})` : `${mesLargo(mes)} ya era igual que ${mesLargo(r.desde)}`);
      cargar();
    } catch (e: any) { toast.error(e?.message || 'No se pudo copiar'); }
  }

  if (error && !d) return <p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-2"><Info size={16} className="mr-1 inline text-brand-blue" />{error}</p>;
  if (!d) return <Skeleton className="h-96 w-full rounded-2xl" />;

  const meses = d.meses.map((m) => m.mes);
  const dif = (m: string) => { const o = d.totales.objetivo[m], r = d.totales.real[m]; return o && r != null ? r - o : null; };

  return (
    <div className={cargando ? 'opacity-60 transition' : 'transition'} data-testid="presupuesto-ingresos">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setDesde(sumaMeses(desde, -1))} title="Un mes antes" aria-label="Un mes antes" className={navBtn}><ChevronLeft size={16} /></button>
          <span className="text-sm font-medium capitalize text-content">{mesLargo(meses[0])} – {mesLargo(meses[meses.length - 1])}</span>
          <button onClick={() => setDesde(sumaMeses(desde, 1))} title="Un mes después" aria-label="Un mes después" className={navBtn}><ChevronRight size={16} /></button>
          {desde !== sumaMeses(mesLocal(), -2) && <button onClick={() => setDesde(sumaMeses(mesLocal(), -2))} className={`${navBtn} text-xs`}>Hoy</button>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-content-2">
            <input type="checkbox" checked={verDetalle} onChange={(e) => setVerDetalle(e.target.checked)} className="accent-current" />
            Ver operativo y real
          </label>
          {puedeEditar && d.disponible && (
            <button onClick={() => setCopiarPeriodo(true)} className={`${navBtn} flex items-center gap-1.5 text-xs`} data-testid="btn-copiar-periodo">
              <CalendarRange size={14} /> Copiar un periodo…
            </button>
          )}
        </div>
      </div>

      {!d.disponible && <p className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">{d.aviso}</p>}

      <p className="mb-3 text-sm text-content-3">
        La meta de ingresos de cada mes, por concepto. Debajo, lo que calcula el sistema (operativo: alumnos activos × su plan; en los demás conceptos, la referencia)
        y lo real devengado. Reportes › Presupuesto vs Real usa este objetivo donde esté capturado.{puedeEditar ? ' Toca una celda para cambiarla.' : ''}
      </p>

      <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
              <th className="sticky left-0 z-10 bg-surface px-4 py-2 font-medium">Concepto</th>
              {d.meses.map((m) => (
                <th key={m.mes} className={`px-2 py-2 text-right font-medium ${m.enCurso ? 'text-brand-blue' : ''}`}>
                  <div className="flex items-center justify-end gap-1">
                    {mesCorto(m.mes)}{m.enCurso ? ' ·hoy' : ''}
                    {puedeEditar && d.disponible && (
                      <button onClick={() => setCopiar(m.mes)} title={`Copiar a ${mesLargo(m.mes)} lo de ${mesLargo(sumaMeses(m.mes, -1))}`}
                        className="rounded p-0.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><Copy size={12} /></button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.filas.map((f) => (
              <Fragment key={f.k}>
                <tr className="border-b border-line/40">
                  <td className="sticky left-0 z-10 bg-surface px-4 py-1.5 text-content">
                    <span className="font-medium">{f.categoria}</span>{f.concepto !== f.categoria && <span className="ml-1 text-xs text-content-3">{f.concepto}</span>}
                  </td>
                  {meses.map((m) => <Celda key={m} c={f.celdas[m]} puede={puedeEditar && d.disponible} onClick={() => setEdit({ fila: f, mes: m, celda: f.celdas[m] })} />)}
                </tr>
                {verDetalle && (
                  <>
                    <tr className="text-xs text-content-3">
                      <td className="sticky left-0 z-10 bg-surface py-0.5 pl-8 pr-2">Operativo</td>
                      {meses.map((m) => <td key={m} className="px-2 py-0.5 text-right tabular-nums" title={f.operativo[m]?.origen || ''}>{f.operativo[m] ? sinCentavos(f.operativo[m]!.amount) : '—'}</td>)}
                    </tr>
                    <tr className="border-b border-line/40 text-xs text-content-3">
                      <td className="sticky left-0 z-10 bg-surface py-0.5 pb-1.5 pl-8 pr-2">Real</td>
                      {meses.map((m) => <td key={m} className="px-2 py-0.5 pb-1.5 text-right tabular-nums">{f.real[m] == null ? '—' : sinCentavos(f.real[m]!)}</td>)}
                    </tr>
                  </>
                )}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-line bg-surface-2/70 font-semibold">
              <td className="sticky left-0 z-10 bg-surface px-4 py-2 text-content">Objetivo total</td>
              {meses.map((m) => <td key={m} className="px-2 py-2 text-right tabular-nums text-content" data-testid={`obj-${m}`}>{d.totales.objetivo[m] ? sinCentavos(d.totales.objetivo[m]) : '—'}</td>)}
            </tr>
            <tr className="text-content-3">
              <td className="sticky left-0 z-10 bg-surface px-4 py-1.5">Operativo (el sistema)</td>
              {meses.map((m) => <td key={m} className="px-2 py-1.5 text-right tabular-nums">{sinCentavos(d.totales.operativo[m] || 0)}</td>)}
            </tr>
            <tr className="text-content-3">
              <td className="sticky left-0 z-10 bg-surface px-4 py-1.5">Real devengado</td>
              {meses.map((m) => <td key={m} className="px-2 py-1.5 text-right tabular-nums">{d.totales.real[m] == null ? '—' : sinCentavos(d.totales.real[m]!)}</td>)}
            </tr>
            <tr className="border-t border-line/60">
              <td className="sticky left-0 z-10 bg-surface px-4 py-1.5 text-content-2">Real − objetivo</td>
              {meses.map((m) => { const v = dif(m); return <td key={m} className={`px-2 py-1.5 text-right tabular-nums ${colorDif(v)}`}>{v == null ? '—' : signo(v)}</td>; })}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-3">
        <span><b className="text-content-2">$1,000</b> capturado en ese mes</span>
        <span className="flex items-center gap-1"><Repeat size={11} /> se repite desde un mes anterior</span>
        <span><span className="rounded bg-amber-100 px-1 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">solo</span> solo ese mes</span>
        <span>Operativo: mensualidades = {d.alumnas} alumnos activos × su plan; los demás, promedio de 3 meses (futuros: como el mes en curso)</span>
      </div>

      <section className="mt-6" data-testid="comparativo-ingresos">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-bold text-content">Comparativo del mes: objetivo vs operativo vs real</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setMesComp(sumaMeses(mesComp, -1))} title="Mes anterior" aria-label="Mes anterior" className={navBtn}><ChevronLeft size={16} /></button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium capitalize text-content" data-testid="comparativo-ingresos-titulo">{mesLargo(mesComp)}</span>
            <button onClick={() => setMesComp(sumaMeses(mesComp, 1))} title="Mes siguiente" aria-label="Mes siguiente" className={navBtn}><ChevronRight size={16} /></button>
            {mesComp !== mesLocal() && <button onClick={() => setMesComp(mesLocal())} className={`${navBtn} text-xs`}>Este mes</button>}
          </div>
        </div>
        <Comparativo key={`${mesComp}-${version}`} mes={mesComp} />
      </section>

      {edit && <EditorObjetivo e={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); cargar(); }} />}
      {copiar && (
        <ConfirmDialog title={`Copiar a ${mesLargo(copiar)}`} tone="normal" confirmLabel="Copiar"
          lines={[`${mesLargo(copiar)} queda igual que ${mesLargo(sumaMeses(copiar, -1))}, concepto por concepto.`, 'Solo cambia ese mes: los meses de antes y de después se quedan como están.']}
          onConfirm={() => { const m = copiar; setCopiar(null); hacerCopia(m); }} onClose={() => setCopiar(null)} />
      )}
      {copiarPeriodo && <CopiarPeriodoDialog tipo="ingresos" desdeInicial={meses[0]} onClose={() => setCopiarPeriodo(false)} onCopiado={() => { setCopiarPeriodo(false); cargar(); }} />}
    </div>
  );
}

function Celda({ c, puede, onClick }: { c: PICelda | null; puede: boolean; onClick: () => void }) {
  const cls = `px-2 py-1.5 text-right tabular-nums ${puede ? 'cursor-pointer hover:bg-accent/10' : ''}`;
  const titulo = c ? (c.origen === 'heredado' ? `Se repite desde ${mesLargo(c.desde || '')}` : c.origen === 'solo' ? 'Solo este mes' : 'Capturado en este mes; se repite en los siguientes') + (c.note ? ` · ${c.note}` : '') : (puede ? 'Sin objetivo: toca para capturarlo' : 'Sin objetivo');
  return (
    <td className={cls} title={titulo} onClick={puede ? onClick : undefined}>
      {!c ? <span className="text-content-3">—</span>
        : c.origen === 'heredado' ? <span className="text-content-3">{c.amount ? sinCentavos(c.amount) : '$0'}</span>
        : <span className="font-medium text-content">{sinCentavos(c.amount)}{c.origen === 'solo' && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-normal text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">solo</span>}</span>}
    </td>
  );
}

const input = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue';
const btn = 'rounded-lg px-4 py-2 text-sm font-medium transition disabled:opacity-50';

function EditorObjetivo({ e, onClose, onSaved }: { e: Edit; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const c = e.celda;
  const op = e.fila.operativo[e.mes];
  const [monto, setMonto] = useState(c ? String(c.amount) : '');
  const [modo, setModo] = useState<'desde' | 'solo'>(c?.origen === 'solo' ? 'solo' : 'desde');
  const [nota, setNota] = useState(c?.note || '');
  const [guardando, setGuardando] = useState(false);
  const propio = c && (c.origen === 'desde' || c.origen === 'solo');
  async function guardar() {
    const a = Number(String(monto).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(a) || a < 0) { toast.error('Escribe un monto válido'); return; }
    setGuardando(true);
    try { await fijarMontoIngreso({ cat_key: e.fila.k, month: e.mes, amount: a, modo, note: nota.trim() || null }); toast.success('Objetivo guardado'); onSaved(); }
    catch (er: any) { toast.error(er?.message || 'No se pudo guardar'); } finally { setGuardando(false); }
  }
  async function quitar() {
    setGuardando(true);
    try { await quitarMontoIngreso(e.fila.k, e.mes); toast.success('Objetivo quitado'); onSaved(); }
    catch (er: any) { toast.error(er?.message || 'No se pudo quitar'); } finally { setGuardando(false); }
  }
  return (
    <Modal titulo={`${e.fila.categoria} · ${mesLargo(e.mes)}`} onClose={onClose}>
      <p className="text-content-3">
        {!c ? 'Este mes no tiene objetivo.' : c.origen === 'heredado' ? `Hoy: ${money(c.amount)}, se repite desde ${mesLargo(c.desde || '')}.` : c.origen === 'solo' ? `Hoy: ${money(c.amount)}, solo este mes.` : `Hoy: ${money(c.amount)}, desde este mes.`}
        {op && <> El sistema calcula <b className="text-content">{money(op.amount)}</b> <span className="text-xs">({op.origen})</span>.</>}
      </p>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-content-2">Objetivo del mes</span>
        <input autoFocus inputMode="decimal" value={monto} onChange={(ev) => setMonto(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && guardar()} className={input} placeholder="0.00" />
      </label>
      {op && op.amount > 0 && <button type="button" onClick={() => setMonto(String(Math.round(op.amount)))} className="text-xs text-brand-blue hover:underline">Usar el operativo ({sinCentavos(op.amount)})</button>}
      <fieldset className="space-y-1.5">
        <label className="flex items-start gap-2"><input type="radio" checked={modo === 'desde'} onChange={() => setModo('desde')} className="mt-1" />
          <span><b className="text-content">Desde {mesLargo(e.mes)} en adelante</b><span className="block text-xs text-content-3">Se repite cada mes hasta que lo cambies. Los meses anteriores no cambian.</span></span></label>
        <label className="flex items-start gap-2"><input type="radio" checked={modo === 'solo'} onChange={() => setModo('solo')} className="mt-1" />
          <span><b className="text-content">Solo {mesLargo(e.mes)}</b><span className="block text-xs text-content-3">Un mes especial (por ejemplo, un festival). Los demás meses se quedan como están.</span></span></label>
      </fieldset>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-content-2">Nota (opcional)</span>
        <input value={nota} onChange={(ev) => setNota(ev.target.value)} maxLength={190} className={input} placeholder="Ej. 240 alumnos × $850" />
      </label>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        {propio ? <button onClick={quitar} disabled={guardando} className={`${btn} text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40`} title="Este mes vuelve a tomar el objetivo del mes anterior que se repite">Quitar</button> : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className={`${btn} text-content-2 hover:bg-surface-2`}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} className={`${btn} bg-accent text-on-accent`}>Guardar</button>
        </div>
      </div>
    </Modal>
  );
}

function Comparativo({ mes }: { mes: string }) {
  const [d, setD] = useState<PIComparativo | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setD(null); setError(null);
    getComparativoIngresos(mes).then((r) => { if (vivo) setD(r); }).catch((e: any) => { if (vivo) setError(e?.message || 'No se pudo cargar'); });
    return () => { vivo = false; };
  }, [mes]);
  if (error) return <p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-2">{error}</p>;
  if (!d) return <Skeleton className="h-48 w-full rounded-2xl" />;
  const t = d.totales;
  return (
    <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm">
      {d.vacio && <p className="px-4 pt-3 text-xs text-content-3">Este mes no tiene objetivo capturado: se comparan solo operativo y real.</p>}
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
            <th className="px-4 py-2 font-medium">Concepto</th>
            <th className="px-2 py-2 text-right font-medium">Objetivo</th>
            <th className="px-2 py-2 text-right font-medium">Operativo</th>
            <th className="px-2 py-2 text-right font-medium">{d.futuro ? 'Real (aún no)' : 'Real'}</th>
            <th className="px-4 py-2 text-right font-medium">Real − objetivo</th>
          </tr>
        </thead>
        <tbody>
          {d.filas.map((f, i) => (
            <tr key={f.k} className={`border-b border-line/60 ${i % 2 ? 'bg-surface-2/40' : ''}`}>
              <td className="px-4 py-2 text-content">{f.categoria}{f.concepto !== f.categoria && <span className="ml-1 text-xs text-content-3">{f.concepto}</span>}</td>
              <td className="px-2 py-2 text-right tabular-nums text-content" title={f.note || ''}>{f.objetivo == null ? <span className="text-content-3">—</span> : money(f.objetivo)}</td>
              <td className="px-2 py-2 text-right tabular-nums text-content-3" title={f.origenOperativo || ''}>{f.operativo == null ? '—' : money(f.operativo)}</td>
              <td className="px-2 py-2 text-right font-semibold tabular-nums text-content">{f.real == null ? <span className="font-normal text-content-3">—</span> : money(f.real)}</td>
              <td className={`px-4 py-2 text-right tabular-nums ${colorDif(f.diferencia)}`}>{f.diferencia == null ? '—' : <>{signo(f.diferencia)} <span className="text-xs">{pctTxt(f.pct)}</span></>}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-line bg-surface-2/70 font-semibold">
            <td className="px-4 py-2 text-content">Total</td>
            <td className="px-2 py-2 text-right tabular-nums text-content" data-testid="comp-objetivo">{t.objetivo ? money(t.objetivo) : '—'}</td>
            <td className="px-2 py-2 text-right tabular-nums text-content-2">{money(t.operativo)}</td>
            <td className="px-2 py-2 text-right tabular-nums text-content">{t.real == null ? '—' : money(t.real)}</td>
            <td className={`px-4 py-2 text-right tabular-nums ${colorDif(t.diferencia)}`} title={t.realConObjetivo != null && t.realConObjetivo !== t.real ? `Solo los conceptos con objetivo: real ${money(t.realConObjetivo)}` : ''}>
              {t.diferencia == null ? '—' : <>{signo(t.diferencia)} <span className="text-xs">{pctTxt(t.pct)}</span></>}
            </td>
          </tr>
        </tfoot>
      </table>
      <p className="px-4 py-2 text-xs text-content-3">{d.notas[0]}</p>
    </div>
  );
}
